import React, { useEffect, useRef, useState, CSSProperties } from 'react';
import styled from 'styled-components';
import InputBase from '../InputStyle';
import InputValidMessage from '@components/dataDisplay/InputValidMessage';
import LabelSet from '../Label/LabelSet';

const LabelWrapper = styled.label<{
  direction?: 'row' | 'column';
  labelWidth?: string | number;
}>`
  display: flex;
  flex-direction: ${({ direction }) => direction || 'column'};
  align-items: ${({ direction }) =>
    direction === 'row' ? 'center' : 'flex-start'};
  gap: ${({ direction }) => (direction === 'row' ? '12px' : '4px')};
  width: 100%;
  pointer-events: auto;
  user-select: auto;
`;

interface IinfoLink {
  link?: string;
  title?: string;
  tooltip?: string;
}

export interface TextInputProps {
  value: string | number | readonly string[] | undefined;
  onChange: React.ChangeEventHandler<HTMLInputElement> | undefined;
  name: string | undefined;
  shape?: string;
  color?: string;
  type?: string;
  maxLength?: number;
  isValid?: boolean;
  validMessage?: string;
  block?: boolean;
  placeholder?: string;
  label?: string;
  required?: boolean;
  onKeyPress?: React.KeyboardEventHandler<HTMLInputElement> | undefined;
  min?: number;
  max?: number;
  width?: number | string;
  disabled?: boolean;
  onFocus?: () => void;
  onClick?: () => void;
  infoLink?: IinfoLink;
  inputStyle?: CSSProperties;
  style?: CSSProperties;
  direction?: 'row' | 'column';
  labelWidth?: string | number;
  description?: string;
  align?: 'left' | 'center' | 'right'; // 🆕 정렬 옵션
  [propName: string]: any;
}

function TextInput({
  value,
  onChange,
  type = 'text',
  name,
  maxLength = 100,
  color,
  isValid,
  validMessage,
  placeholder,
  onKeyPress,
  label,
  required,
  width,
  min,
  max,
  disabled,
  onFocus,
  onClick,
  infoLink,
  inputStyle,
  style,
  direction = 'column',
  labelWidth,
  description,
  align = 'left',
}: TextInputProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const isComposingRef = useRef(false);
  const [composingValue, setComposingValue] = useState('');

  useEffect(() => {
    if (!isComposingRef.current) {
      setComposingValue(value == null ? '' : String(value));
    }
  }, [value]);

  function handleOnChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (isComposingRef.current) {
      setComposingValue(e.target.value);
      return;
    }
    if (onChange) {
      onChange(e);
    }
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: direction || 'column',
        alignItems: direction === 'row' ? 'center' : 'flex-start',
        gap: direction === 'row' ? '12px' : '4px',
        width: '100%',
        ...style,
      }}
      onClick={onClick}
    >
      {label && (
        <LabelSet
          required={required}
          infoLink={infoLink}
          label={label}
          description={description}
          labelWidth={labelWidth || (direction === 'row' ? 'auto' : '100%')}
          align={align}
        />
      )}

      <div style={{ width: '100%', pointerEvents: 'auto', userSelect: 'auto' }}>
        <InputBase
          ref={inputRef}
          name={name}
          id={name}
          value={isComposingRef.current ? composingValue : value || ''}
          onChange={handleOnChange}
          onCompositionStart={() => {
            isComposingRef.current = true;
            setComposingValue(value == null ? '' : String(value));
          }}
          onCompositionEnd={(e: React.CompositionEvent<HTMLInputElement>) => {
            isComposingRef.current = false;
            setComposingValue(e.currentTarget.value);
            if (onChange) {
              onChange({
                target: e.currentTarget,
                currentTarget: e.currentTarget,
              } as React.ChangeEvent<HTMLInputElement>);
            }
          }}
          style={{
            width:
              direction === 'row' && labelWidth
                ? '100%'
                : typeof width === 'number'
                  ? `${width}px`
                  : width || '100%',
            ...inputStyle,
          }}
          type={type}
          color={color}
          maxLength={maxLength}
          max={max}
          min={min}
          isValid={isValid}
          placeholder={placeholder}
          onKeyPress={onKeyPress}
          disabled={disabled}
          onFocus={onFocus}
        />
        {isValid && validMessage && (
          <InputValidMessage>{validMessage}</InputValidMessage>
        )}
      </div>
    </div>
  );
}

export default React.memo(TextInput);
